import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
}                                                           from '@angular/core';
import OlMap                                                from 'ol/Map';
import OlView                                                                          from 'ol/View';
import {ApBaseVectorLayer, ApDistributionClassColors, LayerGroupNames, MAP_PROJECTION} from '../../ap-map';
import {DomSanitizer, SafeUrl}                                                         from '@angular/platform-browser';
import {MapStore}                                           from '../../stores/map/map.store';
import OlStyle                                              from 'ol/style/Style';
import OlVectorSource                                       from 'ol/source/Vector';
import {APP_CONFIGURATION}                                  from '../../ap-core/config';
import {MapFactoryLayer}                                    from '../../ap-map/layers/map-factory-layer';
import BaseLayer                                            from 'ol/layer/Base';
import OlVectorLayer                                        from 'ol/layer/Vector';
import VectorLayer                                          from 'ol/layer/Vector';
import VectorSource                                         from 'ol/source/Vector';
import JSONFeature                                          from 'ol/format/JSONFeature';
import {GeoJSON}                                            from 'ol/format';
import OlStyleFill                                          from 'ol/style/Fill';
import OlStyleStroke                                        from 'ol/style/Stroke';
import OlStyleText                                          from 'ol/style/Text';
import {ApElementOxydTypeEnum}                              from '../../ap-interface';

@Component({
  selector: 'ap-layer-export',
  template: `
    <div #exportMap [hidden]="noData || pngGenerated" class="ap-layer-export-map"></div>
    <img [src]="mapImgSource" [hidden]="noData || !pngGenerated" class="ap-layer-export-image">
  `,
  styleUrls: ['ap-layer-export.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class ApLayerExportComponent implements AfterViewInit, OnDestroy {
  /**
   * Event is raised when the PNG is generated and rendered from open-layers map.
   */
  @Output()
  public renderCompleted = new EventEmitter<any>();

  /**
   * source layer (containing vector features)
   */
  @Input()
  public sourceLayer: ApBaseVectorLayer;

  /**
   * feature id of the sourceLayer
   */
  @Input()
  public featureId: any;

  /**
   * style of the source layer
   */
  @Input()
  public olStyle: OlStyle;

  /**
   * (optional) Url for an additional map-server layer
   */
  @Input()
  public mapUrl: string;

  // featureUrl related properties
  @Input()
  public featureUrl!: string;

  @Input()
  public featureColorPropertyName: string;

  @Input()
  public featureColorsArray: Array<string> = [];

  @Input()
  public textProperty: string;

  @Input()
  public textPropertyPrecision = 0;

  @Input()
  public textPropertyFont = 'bold 32px Titillium Web, sans-serif';

  @Input()
  public textPropertyFunction!: (value: number, elem: ApElementOxydTypeEnum, method: string) => number;

  @Input()
  textPropertyFunctionParam!: {elem: ApElementOxydTypeEnum, method: string};

  // overlayUrl related properties
  @Input()
  public overlayUrl!: string;

  @Input()
  public overlayTextPropertyName!: string;

  public map: OlMap;
  public mapImgSource: SafeUrl;
  public noData = false;
  public pngGenerated = false;
  private readonly SaveTimeout = 30000;
  private saveTimeoutHandle: any;
  private tilesLoading = 0;
  private tilesLoaded = 0;
  private tilesLoadComplete = false;
  private mapFactoryExportLayer: MapFactoryLayer;
  private vectorExportLayer: OlVectorLayer;

  @ViewChild('exportMap', {static: true}) private _exportMap: ElementRef;

  constructor(private elem: ElementRef,
              private mapStore: MapStore,
              private domSanitizer: DomSanitizer) {
    this.clearMapImage();
  }

  ngAfterViewInit(): void {
    this.initOlMap();
  }

  /**
   * Initializes the hidden map with the given features and optional vector-tiles layer
   */
  public initOlMap(): void {
    const source = new OlVectorSource({});
    const exportLayersList: BaseLayer[] = [];
    const feature = this.sourceLayer?.layer?.getSource()?.getFeatures()?.find(f => f?.getId()?.toString() === this.featureId?.toString());
    if (!feature) {
      console.log(`ApLayerExport : no feature found by id ${this.featureId}`);
      // No feature found => nothing to draw
      this.noData = true;
      this.clearMapImage();
      this.renderCompleted?.emit();
      return;
    }

    // create virtual vector layer for export
    const extent = feature?.getGeometry().getExtent();
    source.addFeature(feature);
    this.vectorExportLayer = new OlVectorLayer();
    this.vectorExportLayer.setStyle(this.olStyle ? this.olStyle : this.sourceLayer?.layer?.getStyle());
    this.vectorExportLayer.setSource(source);
    this.vectorExportLayer.setVisible(true);
    exportLayersList.Add(this.vectorExportLayer);

    // if we have an optional map-factory layer => add it for export.
    if (this.mapUrl && this.mapUrl.length > 0) {
      this.mapFactoryExportLayer = new MapFactoryLayer('exportTileLayer', LayerGroupNames.NONE, APP_CONFIGURATION.MapFactory.Address, `${this.mapUrl}`, this.mapStore,
        { legendGetter: () => null });
      this.mapFactoryExportLayer.Visibility = true;
      exportLayersList.Add(this.mapFactoryExportLayer.layer);
    } else if (this.featureUrl && this.featureUrl.length > 0) {
      const layer = new VectorLayer({
        declutter: false,
        style: (feat) => {
          if (!this.featureColorPropertyName && this.featureColorsArray?.length === 0 && this.textProperty?.length > 0) {
            return [
              new OlStyle({
                stroke: new OlStyleStroke({
                  color: '#000',
                  width: 4
                }),
                fill: new OlStyleFill({
                  color: '#000'
                }),
                text: new OlStyleText({
                  font: this.textPropertyFont,
                  text: feat.getProperties()[this.textProperty].toString(),
                })
              })
            ];
          }
          const classPropertyName = Object.keys(feat.getProperties()).filter(k => k === this.featureColorPropertyName);
          let color = 'rgba(240, 10, 0, 1)';

          if (classPropertyName && classPropertyName.length > 0) {
            const classValue = +feat.getProperties()[classPropertyName[0]];
            if (classPropertyName[0].startsWith('Class')) {
              switch (true) {
                case (classValue < 1):
                  color = ApDistributionClassColors.getColorsFromValue(1);
                  break;
                case (classValue < 2):
                  color = ApDistributionClassColors.getColorsFromValue(2);
                  break;
                case (classValue < 3):
                  color = ApDistributionClassColors.getColorsFromValue(3);
                  break;
                case (classValue < 4):
                  color = ApDistributionClassColors.getColorsFromValue(4);
                  break;
                case (classValue < 5):
                  color = ApDistributionClassColors.getColorsFromValue(5);
                  break;
                case (classValue < 6):
                  color = ApDistributionClassColors.getColorsFromValue(6);
                  break;
                case (classValue < 7):
                  color = ApDistributionClassColors.getColorsFromValue(7);
                  break;
                case (classValue < 8):
                  color = ApDistributionClassColors.getColorsFromValue(8);
                  break;
                case (classValue < 9):
                  color = ApDistributionClassColors.getColorsFromValue(9);
                  break;
              }
            }

            if (this.featureColorsArray && this.featureColorsArray.length > 0) {
              color = this.featureColorsArray[classValue - 1];
            }
          }

          const style = new OlStyle({
            fill: new OlStyleFill({
              color,
            })
          });

          if (this.textProperty?.length > 0) {
            let textValue = feat.getProperties()[this.textProperty].toString();
            if (this.textPropertyFunction) {
              textValue = this.textPropertyFunction(+textValue, this.textPropertyFunctionParam.elem, this.textPropertyFunctionParam.method);
            }

            if (this.textPropertyPrecision > 0) {
              textValue = (+textValue).toFixed(this.textPropertyPrecision);
            }

            style.setText(new OlStyleText({
              font: this.textPropertyFont,
              text: textValue,
            }));
          }

          return [style];
        },
        source: new VectorSource({
          format: new GeoJSON(),
          url: this.featureUrl,
        })
      });
      layer.setVisible(true);
      exportLayersList.Add(layer);
    } else {
      this.tilesLoadComplete = true;
    }

    // Overlay layer to display text from different mapfactory source on top of featureUrl layer or mapUrl layer
    if (this.overlayUrl && this.overlayUrl.length > 0) {
      const layer = new VectorLayer({
        declutter: false,
        style: (feat) => {
          const style = new OlStyle({
            fill: new OlStyleFill({
              color: '#000'
            })
          });

          if (this.overlayTextPropertyName?.length > 0) {
            let textValue = feat.getProperties()[this.overlayTextPropertyName].toString();

            if (this.textPropertyFunction) {
              textValue = this.textPropertyFunction(+textValue, this.textPropertyFunctionParam.elem, this.textPropertyFunctionParam.method);
            }

            if (this.textPropertyPrecision > 0) {
              textValue = (+textValue).toFixed(this.textPropertyPrecision);
            }

            style.setText(new OlStyleText({
              font: this.textPropertyFont,
              text: textValue,
            }));
          }

          return [style];
        },
        source: new VectorSource({
          format: new GeoJSON(),
          url: this.overlayUrl,
        })
      });

      layer.setVisible(true);
      exportLayersList.Add(layer);
    }

    this.map = new OlMap({
      target: this._exportMap?.nativeElement,
      layers: exportLayersList,
      controls: [],
      view: new OlView({
        center: [0, 0],
        zoom: 1,
        projection: MAP_PROJECTION
      })
    });

    // Hook to several events to get informed, when map is ready to be printed
    this.hookToMapEvents();

    if (extent) {
      this.map?.getView()?.fit(extent, {
        size: this.map?.getSize(),
        padding: [10, 10, 10, 10],
      });
    }
    this.map?.renderSync();
  }

  /**
   * Disables the filling of the vector layer:
   * If there is a mapFactory layer => no fill for vector layer
   * due to possible 0.5 opacity of mapFactoryLayer, the colors might change
   * if we use fill for vector layer
   * @private
   */
  private disableVectorLayerFill(): void {
    if (!this.olStyle) {
      return;
    }
    this.olStyle?.setFill(undefined);
    this.vectorExportLayer.setStyle(this.olStyle);
  }

  /**
   * Method handles several map events to recognize when the map and its layers have
   * been loaded / rendered completly.
   * Once all tiles are loaded => export png.
   */
  private hookToMapEvents(): void {
    // for tiles layer => check if all tiles loaded
    if (!!this.mapFactoryExportLayer) {
      this.mapFactoryExportLayer.layer.getSource().on('tileloadstart', () => this.tilesLoadingFunc());
      this.mapFactoryExportLayer.layer.getSource().on('tileloadend', () => this.tilesLoadedFunc());
      this.mapFactoryExportLayer.layer.getSource().on('tileloaderror', () => this.tilesLoadedFunc());
    }

    // for vector layer => rendercomplete
    this.map?.on('rendercomplete', () => this.onRenderComplete());

    // Fallback for map export. In some rare cases openlayers might crash which causes the map to freeze
    // => in those cases the pdf export would not work
    // => therefore we are exporting the map (as it is at this moment) to be able to provide a pdf document
    // => the timeoutHandle is cleared when pdf is generated to stop timer in case of success before timeout reached
    this.saveTimeoutHandle = setTimeout(() => {
      this.getPngFromOlMap(true);
    }, this.SaveTimeout);
  }

  private tilesLoadingFunc(): void {
    ++this.tilesLoading;
  }

  private tilesLoadedFunc(): void {
    ++this.tilesLoaded;
    if (this.tilesLoading === this.tilesLoaded) {
      this.mapFactoryExportLayer.layer.getSource().un('tileloadstart', () => this.tilesLoadingFunc());
      this.mapFactoryExportLayer.layer.getSource().un('tileloadend', () => this.tilesLoadedFunc());
      this.mapFactoryExportLayer.layer.getSource().un('tileloaderror', () => this.tilesLoadedFunc());
      this.disableVectorLayerFill(); // if mapFactory tiles are loaded => disable fill of Vector layer

      this.mapFactoryExportLayer.layer.once('postrender', this.tileRenderComplete.bind(this));
      // important to call renderSync on top of the callstack => otherwise the maps were not fully displayed
      setTimeout(() => this.map?.renderSync(), 0);
    }
  }

  // make sure the tiles are not only loaded....but also rendered completly:
  private tileRenderComplete(): void {
    this.tilesLoadComplete = true;
    this.getPngFromOlMap();
  }

  private onRenderComplete(): void {
    this.getPngFromOlMap();
  }

  /**
   * Generates the PNG image data from open-layer map
   * @param forceCompletion
   * @private
   */
  private getPngFromOlMap(forceCompletion = false): void {
    setTimeout(() => {
      // we already exported
      if (this.pngGenerated) {
        return;
      }
      const elements = this.elem.nativeElement.querySelectorAll('.ol-layer canvas');
      // timeout reached => we take it as it is (no map on export)
      if (!elements) {
        if (forceCompletion) {
          this.completeExport(null);
        }
        // try again later on next rendercomplete (vectors) or tilesloaded (mapfactory) event.
        return;
      }

      // check if all layers and tiles are loaded, but if timeout has been reached we take it as it is and continue
      if (this.mapFactoryExportLayer && !this.tilesLoadComplete && !forceCompletion) {
        return;
      }

      const mapCanvas = document.createElement('canvas');
      const size = this.map?.getSize();
      mapCanvas.width = !!size ? size[0] : 0;
      mapCanvas.height = !!size ? size[1] : 0;
      const mapContext = mapCanvas.getContext('2d');

      elements?.forEach(canvas => {
        if (canvas.width > 0 && canvas.height > 0) {
          const opacity = canvas.parentNode.style.opacity;
          mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
          const transform = canvas.style.transform;
          const matrix = transform
            .match(/^matrix\(([^\(]*)\)$/)[1]
            .split(',')
            .map(Number);
          CanvasRenderingContext2D.prototype.setTransform.apply(
            mapContext,
            matrix
          );
          mapContext.drawImage(canvas, 0, 0);
        }
      });

      this.map?.un('rendercomplete', () => this.onRenderComplete());
      this.completeExport(mapCanvas);
    }, 0);
  }

  /**
   * Finalizing the export with building image source, clearing timeout/events and emit final event.
   * @param mapCanvas
   * @private
   */
  private completeExport(mapCanvas: HTMLCanvasElement): void {
    if (!!mapCanvas && mapCanvas.height > 0 && mapCanvas.width > 0) {
      this.mapImgSource = this.domSanitizer.bypassSecurityTrustUrl(mapCanvas?.toDataURL());
    } else {
      this.clearMapImage();
    }

    this.map?.getLayers().forEach(layer => {
      // this.map?.removeLayer(layer);
      layer.dispose();
    });
    this.map?.setTarget(null);
    this.map?.dispose();

    this.pngGenerated = true;
    clearTimeout(this.saveTimeoutHandle); // clear and stop fallback-Timer.
    this.renderCompleted?.emit();
  }

  ngOnDestroy(): void {
    this.mapFactoryExportLayer?.layer?.un('postrender', this.tileRenderComplete);
    this.mapFactoryExportLayer?.layer?.getSource()?.un('tileloadstart', () => this.tilesLoadingFunc());
    this.mapFactoryExportLayer?.layer?.getSource()?.un('tileloadend', () => this.tilesLoadedFunc());
    this.mapFactoryExportLayer?.layer?.getSource()?.un('tileloaderror', () => this.tilesLoadedFunc());
    this.map?.un('rendercomplete', () => this.onRenderComplete());
    this.map?.un('rendercomplete', () => this.onRenderComplete());
    this.map?.setTarget(null);
  }

  /**
   * clears image with proper data value. Otherwise kendo export might not work properly.
   */
  private clearMapImage(): void {
    // we have to use this for empty image. Empty string does not work with kendo export
    this.mapImgSource = this.domSanitizer.bypassSecurityTrustUrl('data:,');
  }
}
